import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { districtAPI } from '../../services/api';
import ErrorModal from '../Common/ErrorModal';
import { AdminPage, StateBlock } from '../Common/admin';

const DistrictView = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const loadDistrict = useCallback(async () => {
    try {
      setLoading(true);
      const response = await districtAPI.getById(id);
      setRecord(response.data);
    } catch (err) {
      setErrorMessage(t('district.view.messages.loadError'));
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadDistrict();
  }, [loadDistrict]);

  const headerActions = (
    <div className="ad-toolbar-group">
      <button type="button" className="ad-btn" onClick={() => navigate('/district')}>
        <i className="fas fa-arrow-right"></i>
        {t('district.view.backToList')}
      </button>
      <button
        type="button"
        className="ad-btn ad-btn-primary"
        onClick={() => navigate(`/district/edit/${id}`)}
      >
        <i className="fas fa-pen"></i>
        {t('district.view.edit')}
      </button>
    </div>
  );

  if (loading) {
    return (
      <AdminPage title={t('district.view.title')}>
        <StateBlock icon="fa-hourglass-half" title={t('common.loading')} />
      </AdminPage>
    );
  }

  if (!record) {
    return (
      <AdminPage title={t('district.view.title')}>
        <StateBlock icon="fa-circle-exclamation" title={t('district.list.messages.noData')} />
        <ErrorModal
          show={showErrorModal}
          message={errorMessage}
          onClose={() => setShowErrorModal(false)}
        />
      </AdminPage>
    );
  }

  const items = [
    { label: t('district.view.fields.districtCode'), value: record.districtCode },
    { label: t('district.view.fields.nameAr'), value: record.districtNameAr },
    { label: t('district.view.fields.nameEng'), value: record.districtNameEng },
    { label: t('district.view.fields.city'), value: record.city?.cityNameEng },
  ];

  return (
    <AdminPage
      title={record.districtNameAr || t('district.view.title')}
      subtitle={record.districtNameEng}
      headerActions={headerActions}
    >
      <div className="ad-detail">
        <div className="ad-detail-grid">
          {items.map((item) => (
            <div key={item.label} className="ad-detail-item">
              <span className="ad-detail-label">{item.label}</span>
              <span className="ad-detail-value">
                {item.value === null || item.value === undefined || item.value === ''
                  ? '-'
                  : item.value}
              </span>
            </div>
          ))}

          <div className="ad-detail-item">
            <span className="ad-detail-label">{t('district.view.fields.isActive')}</span>
            <span>
              <span
                className={`ad-badge ${record.isActive ? 'ad-badge-success' : 'ad-badge-danger'}`}
              >
                {record.isActive ? t('common.active') : t('common.inactive')}
              </span>
            </span>
          </div>
        </div>
      </div>

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </AdminPage>
  );
};

export default DistrictView;
